import React, { useEffect, useState } from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';
import { propTypes } from '../../util/types';

import {
  sendVerificationEmail,
  hasCurrentUserErrors,
  searchFavListings,
  fetchCurrentUser,
  createUserWithDots,
  verifyUserWithDots,
} from '../../ducks/user.duck';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

// Use a reliable external URL to check for internet connection
const URL = 'https://www.google.com';

export const TopbarContainerComponent = props => {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    isAuthenticated,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    onLogout,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    ...rest
  } = props;

  const { likeItems = [] } =
    (!!currentUser?.id && currentUser.attributes.profile.protectedData) || {};

  const [onlineStatus, setOnlineStatus] = useState(true);

  const checkInternetConnection = async () => {
    try {
      const response = await fetch(URL, { method: 'HEAD', mode: 'no-cors' });
      // If the fetch was successful, set online status to true
      if (response) {
        setOnlineStatus(true);
        return true; // Online
      }
    } catch (error) {
      console.log('No internet connection.', error);
      setOnlineStatus(false);
      return false; // Offline
    }
  };

  useEffect(() => {
    // Check the online status when the component mounts
    checkInternetConnection();

    // Optional: Set an interval to check online status periodically
    const intervalId = setInterval(checkInternetConnection, 5000); // Check every 10 seconds

    return () => clearInterval(intervalId); // Clear interval on unmount
  }, []);

  useEffect(() => {
    try {
      likeItems.length > 0 &&
        dispatch(searchFavListings(likeItems, config)).then(e => {
          if (e) {
            dispatch(fetchCurrentUser());
          }
        });
    } catch (error) { }
  }, [likeItems.length > 0]);

  return (
    <Topbar
      authInProgress={authInProgress}
      currentPage={currentPage}
      currentSearchParams={currentSearchParams}
      currentUser={currentUser}
      currentUserHasListings={currentUserHasListings}
      currentUserHasOrders={currentUserHasOrders}
      history={history}
      isAuthenticated={isAuthenticated}
      authScopes={authScopes}
      location={location}
      notificationCount={notificationCount}
      onLogout={onLogout}
      onManageDisableScrolling={onManageDisableScrolling}
      onResendVerificationEmail={onResendVerificationEmail}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      showGenericError={hasGenericError || !onlineStatus}
      {...rest}
    />
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    createUserInProgress,
    createUserSuccess,
    createUserError,
    verifyUserInProgress,
    verifyUserSuccess,
    verifyUserError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount,
    isAuthenticated,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
    createUserInProgress,
    createUserSuccess,
    createUserError,
    verifyUserInProgress,
    verifyUserSuccess,
    verifyUserError,
    isLoggedInAs,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onCreateUserWithDots: data => dispatch(createUserWithDots(data)),
  onVerifyUserWithDots: data => dispatch(verifyUserWithDots(data)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
